// Детали уведомления: событие — его карточка, сводка — счётчики по типам и карточки событий,
// сводка после закрытия смены — её цифры (ShiftSummaryDetails), отзыв пользователя — FeedbackDetails,
// новые минусы на складе — StockNegativeDetails. Карточки — те же, что в листе событий (EventCard).
import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ResponsiveDialog from '../ResponsiveDialog';
import { EventCard } from '../events/EventDetailsSheet';
import { EVENT_TYPE_GROUPS, EVENT_TYPE_NAMES, GROUP_COLORS, sortByDateDesc } from '../events/eventMeta';
import { CARD_LABEL_COLOR, MUTED_TEXT_COLOR } from '../../styles/tokens';
import { formatNotificationTime, notificationLook } from './notificationMeta';
import ShiftSummaryDetails from './ShiftSummaryDetails';
import AlertMuteControl from './AlertMuteControl';
import FeedbackDetails from './FeedbackDetails';
import StockNegativeDetails from './StockNegativeDetails';

// Виды со своими деталями: без общего блока событий и текста
const OWN_DETAILS = new Set(['shift_summary', 'feedback', 'stock']);

const cardAccent = (event, fallback) => GROUP_COLORS[EVENT_TYPE_GROUPS[event.type]] || fallback;

export default function NotificationDetailsDialog({ item, open, onClose }) {
  if (!item) return null;
  const { color, Icon } = notificationLook(item);
  const payload = item.payload || {};
  const events = item.kind === 'event'
    ? [payload.event].filter(Boolean)
    : sortByDateDesc(payload.events || []);
  const counts = Object.entries(payload.counts || {});
  const totalInDigest = counts.reduce((sum, [, n]) => sum + n, 0);

  return (
    <ResponsiveDialog open={open} onClose={onClose} labelledBy="notification-details-title">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Заголовок бывает в 2 строки (сводка смены): по верху и без отрицательного отступа на телефоне — иначе
            верх уезжает за прокручиваемую область листа и обрезается */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mt: { xs: 0, md: -1 }, mr: -1 }}>
          <Box
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '11px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${color}26`,
            }}
          >
            <Icon sx={{ fontSize: 22, color }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="notification-details-title" component="h2" sx={{ fontSize: '17px', fontWeight: 800, color: '#fff', lineHeight: 1.3 }}>
              {item.title}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
              {formatNotificationTime(item.created_at)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Закрыть" sx={{ width: 44, height: 44, flexShrink: 0, color: CARD_LABEL_COLOR }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {item.kind === 'shift_summary' && <ShiftSummaryDetails item={item} onClose={onClose} />}
        {item.kind === 'feedback' && <FeedbackDetails item={item} />}
        {item.kind === 'stock' && <StockNegativeDetails item={item} onClose={onClose} />}

        {item.kind === 'digest' && counts.length > 0 && (
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {counts.map(([type, n]) => (
              <Box component="li" key={type} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '14px' }}>
                <Box component="span" sx={{ color: '#e4e4e7' }}>{EVENT_TYPE_NAMES[type] || type}</Box>
                <Box component="span" sx={{ color: '#fff', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{n}</Box>
              </Box>
            ))}
          </Box>
        )}

        {OWN_DETAILS.has(item.kind) ? null : events.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {events.map((event, index) => (
              <EventCard key={event.id || index} event={event} accent={cardAccent(event, color)} singleDay={false} />
            ))}
            {item.kind === 'digest' && totalInDigest > events.length && (
              <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
                {`Показаны последние ${events.length} из ${totalInDigest}. Все события — на вкладке «События».`}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '14px', color: '#e4e4e7' }}>{item.body}</Typography>
        )}

        {/* Оповещение о сбое по серверу или ключу — «Не присылать по …» / «Вернуть» */}
        {item.kind === 'alert' && <AlertMuteControl item={item} />}
      </Box>
    </ResponsiveDialog>
  );
}
